/*
 * Adapptio
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	IEditorApiCompileError,
	IEditorApiDocumentError,
	IEditorApiErrorListResponse,
	IEditorApiRuntimeError
} from "@adapptio/lib-core";
import { IErrorListGroup } from "../ui/components/ErrorList/ErrorListTypes";
import {
	DOC_ERROR_SEVERITY,
	ICompileError,
	IDocumentError,
	IRuntimeError
} from "@adapptio/lib-blueprint";
import { IErrorGroup, IErrorListData, TErrorsSeverityCount } from "./ErrorsTypes";
import { isNil } from "../../../lib-shared";

export function getEmptySeverityCount(): TErrorsSeverityCount {
	return {
		[DOC_ERROR_SEVERITY.ERROR]: 0,
		[DOC_ERROR_SEVERITY.WARNING]: 0,
		[DOC_ERROR_SEVERITY.INFO]: 0,
		[DOC_ERROR_SEVERITY.HINT]: 0
	};
}

/**
 * Map ErrorList type from api to ErrorGroups type
 *
 * @param errorList
 */
export function processEditorApiErrorList(errorList: IEditorApiErrorListResponse): IErrorListData {
	const values = Object.values(errorList);
	const errorListData: IErrorListData = {
		errorGroups: new Map(),
		severityCount: {
			...getEmptySeverityCount()
		},
		totalCount: 0
	};

	const incrementSeverityCount = (errorItem: ICompileError | IDocumentError | IRuntimeError) => {
		if (isNil(errorItem.severity)) {
			return;
		}

		errorListData.severityCount[errorItem.severity] += 1;
	};

	for (let i = 0; i < values.length; i++) {
		const { compileErrors, runtimeErrors, parseErrors, uri, id } = values[i];

		const errorGroup: IErrorGroup = {
			documentUri: uri,
			resourceId: id,
			compileErrors: [],
			parseErrors: [],
			runtimeErrors: []
		};

		// Iterate only the longest length to improve performance
		const length = Math.max(compileErrors.length, runtimeErrors.length, parseErrors.length);

		for (let j = 0; j < length; j++) {
			const compileError = compileErrors[j];
			const parseError = parseErrors[j];
			const runtimeError = runtimeErrors[j];

			// Compile error
			if (!isNil(compileError)) {
				const processedCompileError = processApiCompileError(compileError);
				incrementSeverityCount(processedCompileError);
				errorGroup.compileErrors.push(processedCompileError);
			}

			// Parse error
			if (!isNil(parseError)) {
				const processedParseError = processApiParseError(parseError);
				incrementSeverityCount(processedParseError);
				errorGroup.parseErrors.push(processedParseError);
			}

			// Runtime error
			if (!isNil(runtimeError)) {
				const processedRuntimeError = processApiRuntimeError(runtimeError);
				incrementSeverityCount(processedRuntimeError);
				errorGroup.runtimeErrors.push(processedRuntimeError);
			}
		}

		errorListData.totalCount +=
			errorGroup.compileErrors.length + errorGroup.parseErrors.length + errorGroup.runtimeErrors.length;

		errorListData.errorGroups.set(uri, errorGroup);
	}

	return errorListData;
}

export function processApiCompileError(compileError: IEditorApiCompileError): ICompileError {
	return {
		...compileError,
		modelNodeId: null
	};
}

export function processApiParseError(parseError: IEditorApiDocumentError): IDocumentError {
	return {
		...parseError
	};
}

export function processApiRuntimeError(runtimeError: IEditorApiRuntimeError): IRuntimeError {
	return {
		...runtimeError,
		modelNodeId: null,
		severity: runtimeError.severity || null,
		name: runtimeError.name || null,
		message: runtimeError.message || null,
		modelPath: runtimeError.modelPath || null
	};
}

export function errorListGroupHasFatalErrors(errors: IErrorListGroup[]): boolean {
	return errors.some((errorGroup) =>
		[ errorGroup.parseErrors, errorGroup.compileErrors, errorGroup.runtimeErrors ].some((errs) =>
			errs.some((error) => error.severity === DOC_ERROR_SEVERITY.ERROR)
		)
	);
}
